import { resolveDocumentContentType } from '@/utils/resolveDocumentContentType';
import { toast } from '@heidi/design-system/toast';

import { useSessionEhrProvider } from '@/hooks/integrations/useSessionEhrProvider';
import {
  TabType,
  useSessionWindow,
  useSessionWindows,
} from '@/hooks/query/useQuerySessionWindows';
import { useIsFreeUser } from '@/hooks/useIsFreeUser';
import { StreamingStatus } from '@/models/scribeRecordModel';
import { updateDocument } from '@/pages/api/scribeAPI';
import { postCreateEmptyDocument } from '@/pages/api/scribeAPIV2';
import { type GetDocumentResponse } from '@/types';
import { mapNewBrain } from '@/utils/brainUtils';
import { logger } from '@/utils/logger/logger';
import {
  ContentType,
  DocumentGenType,
  DocumentTabType,
  GenerationMethod,
  type Brain,
} from '@heidi/schema/ml-scribe';
import { mockEmptyDocument, type GetDocumentsResponse } from '@heidi/session/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { get } from 'lodash-es';
import { GET_DOCUMENT_BY_ID } from '../query/useQueryDocumentById';
import { GET_DOCUMENTS_BY_ID } from '../query/useQueryDocumentsBySessionId';
import { useEnableBlockEditorV1 } from '../useEnableBlockEditorV1';

type Props = {
  content: string;
  aiCommandText: string;
  brain?: Brain;
  name?: string;
  contentType?: ContentType;
};

export function useMutateCreateDocumentFromAskAI(sessionId: string) {
  const queryClient = useQueryClient();

  const { activeWindowIndex } = useSessionWindows({ sessionId });
  const { addTabAndSetActive } = useSessionWindow({
    sessionId,
    windowIndex: activeWindowIndex,
  });

  const { sessionEhrProvider } = useSessionEhrProvider(sessionId);
  const enableBlockEditorV1 = useEnableBlockEditorV1(sessionEhrProvider);
  const { isFreeUser } = useIsFreeUser();

  //   const { mutate: updateDocument } = useMutationUpdateDocumentById(sessionId);
  return useMutation({
    mutationKey: ['createDocument', { sessionId }],
    mutationFn: async (input: Props) => {
      const documents = queryClient.getQueryData<GetDocumentsResponse>([
        GET_DOCUMENTS_BY_ID,
        { sessionId: sessionId },
      ]);
      // Create empty document
      const documentResponse = await postCreateEmptyDocument({
        contentType: resolveDocumentContentType(sessionId),
        sessionId,
        documentTabType: DocumentTabType.DOCUMENT,
        generationMethod: GenerationMethod.ASK_AI,
        aiCommandText: input.aiCommandText,
        brain: input.brain,
        index: documents?.total,
      });

      //   await updateDocument({
      //     content: input.content,
      //     documentId: documentResponse.data.id,
      //   });
      documentResponse.data.brain = mapNewBrain(documentResponse.data.brain, isFreeUser);
      const document = documentResponse.data;

      const data = await updateDocument({
        sessionId,
        documentId: document.id,
        name: input.name || document?.name || '',
        content: input.content,
        generationType: DocumentGenType.ASK_AI, // Force ASK_AI for Ask Heidi created documents
        updatedDictation: document?.updated_dictation || '',
        contentType:
          input.contentType ||
          (enableBlockEditorV1 ? ContentType.HTML : ContentType.MARKDOWN),
      });

      return { documentResponse, data };
    },
    onSuccess: ({ documentResponse }, { content }) => {
      // await queryClient.invalidateQueries({
      //   queryKey: [GET_DOCUMENTS_BY_ID, { sessionId: sessionId }],
      // });

      queryClient.setQueryData<GetDocumentsResponse>(
        [GET_DOCUMENTS_BY_ID, { sessionId: sessionId }],
        (oldData) => {
          if (!oldData) {
            return undefined;
          }

          const docs = oldData.documents || [];

          if (docs) {
            docs.push({
              ...documentResponse.data,
              id: documentResponse.data.id,
              generation_type: documentResponse.data.generation_type,
              created_at: new Date().toISOString(),
              session_id: sessionId,
              content,
            });
          }

          const newData: GetDocumentsResponse = {
            ...oldData,
          };
          return newData;
        }
      );
    },
    onMutate: (input) => {
      // optimistically update the cache
      const documents = queryClient.getQueryData<GetDocumentsResponse>([
        GET_DOCUMENTS_BY_ID,
        { sessionId: sessionId },
      ]);
      queryClient.setQueryData<GetDocumentResponse>(
        [GET_DOCUMENT_BY_ID, { sessionId: sessionId, documentId: mockEmptyDocument.id }],
        () => {
          return {
            ...mockEmptyDocument,
            streamingStatus: StreamingStatus.NOT_STARTED,
          };
        }
      );
      const newData: GetDocumentsResponse = {
        documents: [
          ...(documents?.documents || []),
          {
            ...mockEmptyDocument,
            session_id: sessionId,
            name: input.name || input.aiCommandText || 'Untitled Document',
            generation_type: DocumentGenType.ASK_AI,
          },
        ],
        total: (documents?.total || 0) + 1,
      };
      queryClient.setQueryData<GetDocumentsResponse>(
        [GET_DOCUMENTS_BY_ID, { sessionId: sessionId }],
        newData
      );
    },
    onSettled: (data) => {
      if (data?.documentResponse) {
        addTabAndSetActive({
          name: TabType.DOCUMENT,
          documentId: data.documentResponse.data.id,
        });
      }

      void queryClient.invalidateQueries({
        queryKey: [GET_DOCUMENTS_BY_ID, { sessionId: sessionId }],
      });
    },
    onError: (error) => {
      logger.error('Error:', error);

      if (error instanceof AxiosError) {
        toast({
          tone: 'destructive',
          title: 'Failed to create document',
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          description: get(error.response, 'data.detail.message', error.message),
        });
      }
    },
  });
}
